import {
  TransportAuthorization,
  TransportControlChecks,
  TransportOperationType,
} from "../../../../../../passapp-sdk/types/transportTypes";
import { countryList } from "../../../../../../utils/countryCodes";
import {
  AuthorizationGroup,
  AuthorizationPerson,
  AuthorizationRow,
  ExitAuthorization,
  ExitAuthorizationGroup,
  GateControlItem,
  GateControlKey,
  GuardApprovalInfo,
} from "./transportAuthorizationTypes";
import { toTimestampMs } from "./transportDateFormat";

export const OPERATION_LABELS: Record<TransportOperationType, string> = {
  load: "Carga",
  unload: "Descarga",
};

/** Controles que dependen de lo que el autorizante marcó en `controlChecks` (las patentes no
 * entran acá: esas se piden siempre, ver `plateControls` en `toExitAuthorization`). */
type BackendControlKey = keyof TransportControlChecks;

// Etiquetas de los controles de portería: se conservan las que ya usaba la UI.
export const CONTROL_LABELS: Record<BackendControlKey, string> = {
  receipt: "Remito",
  seal: "Precinto",
  vehicleCustody: "Custodio vehicular",
  satelliteCustody: "Custodio satelital",
};

export const CONTROL_ORDER: BackendControlKey[] = [
  "receipt",
  "seal",
  "vehicleCustody",
  "satelliteCustody",
];

// countryList tiene los nombres en inglés; para los países frecuentes en portería preferimos
// el nombre en español, que es lo que la UI venía mostrando.
const COUNTRY_LABEL_OVERRIDES: Record<string, string> = {
  ARG: "Argentina",
  BRA: "Brasil",
  BOL: "Bolivia",
  CHL: "Chile",
  PRY: "Paraguay",
  PER: "Perú",
  URY: "Uruguay",
  USA: "Estados Unidos",
};

/** Un id que puede venir suelto o embebido en el objeto que lo referencia. */
const toId = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.id ? String(value.id) : "";
};

/**
 * Empresa que autorizó el ingreso, para prefillear la solicitud de egreso: el egreso se le pide a
 * la misma empresa que dejó entrar al camión. Se busca primero en la autorización de transporte del
 * evento de entrada, que es la que corresponde exactamente a este flujo, y si no en `company` del
 * evento, donde puede haber más de una: se toma la primera, que es la que la tabla de portería ya
 * muestra como empresa del acceso.
 */
export type EntranceVehiclePrefill = {
  vehiclePlate: string;
  /** Patentes con las que el camión entró (o las que el guardia eligió en el modal de salida).
   * Vacío = sin acoplados. */
  trailerPlates: string[];
  /** Los acoplados del evento de entrada, completos (con id, marca y seguro): el mismo universo
   * que `trailerPlates`, pero no necesariamente el mismo orden ni la misma cantidad — sólo trae
   * ficha completa el que la tenía en el evento. */
  trailers: any[];
  /** true sólo cuando sabemos que el camión egresa sin acoplados, porque el ingreso ya decía "no
   * aplica". Distinto de `trailerPlates` vacío por falta de datos, donde no hay que tocar el
   * campo. */
  trailerNotApplicable: boolean;
};

/** Todas las patentes de acoplado de una autorización de entrada, para prefillear el egreso con
 * las mismas. A diferencia de `resolveTrailerPlate` (que sólo mira la primera, para las vistas de
 * una sola patente que quedan fuera de este cambio) esta usa el array completo. */
const resolveTrailerPlatesFromAuth = (auth: TransportAuthorization): string[] =>
  auth.trailerNotApplicable ? [] : auth.trailerPlates ?? [];

/**
 * Patentes con las que el camión entró, para prefillear el egreso. La estadía no interviene: se
 * declara recién al egresar, así que los acoplados siempre se prefillean y el guardia los saca a
 * mano si el camión se retira sin ellos.
 */
export const resolveEntranceVehiclePrefill = (entranceEvent: any): EntranceVehiclePrefill => {
  const event = entranceEvent?.event || entranceEvent;
  if (!event) {
    return { vehiclePlate: "", trailerPlates: [], trailers: [], trailerNotApplicable: false };
  }

  const entryAuth = event.transportAuthorizations?.[0];
  const vehiclePlate = event.vehicle?.plate || entryAuth?.vehiclePlate || "";

  // Los acoplados reales son los de `trailers`, no los de la autorización: al ingresar el guardia
  // puede haber cargado más o menos de los que la autorización traía. La autorización queda como
  // plan B para los eventos que no traen el vehículo. Los ids sueltos de eventos viejos no traen
  // patente y se descartan.
  const trailers = (event.trailers || []).filter((t: any) => t && typeof t !== "string");
  const trailerPlatesFromEvent: string[] = trailers.map((t: any) => t.plate).filter(Boolean);
  const trailerPlates =
    trailerPlatesFromEvent.length > 0
      ? trailerPlatesFromEvent
      : entryAuth
      ? resolveTrailerPlatesFromAuth(entryAuth)
      : [];

  return {
    vehiclePlate,
    trailerPlates,
    trailers,
    trailerNotApplicable: trailerPlates.length === 0 && !!entryAuth?.trailerNotApplicable,
  };
};

/**
 * Patentes que se le piden a la autorización de egreso.
 *
 * Por defecto son las del ingreso, pero cuando la salida se armó desde el modal de la vista por
 * vehículo mandan las unidades que el guardia eligió ahí: si destildó los acoplados, la
 * autorización tiene que salir con "no aplica" y no con los que traía el ingreso.
 */
export const resolveExitVehiclePrefill = (
  entrancePrefill: EntranceVehiclePrefill,
  selection: {
    exitUnitsPreselected?: boolean;
    vehiclePlate?: string;
    trailerVehicles?: { vehiclePlate?: string }[];
  }
): EntranceVehiclePrefill => {
  if (!selection.exitUnitsPreselected) return entrancePrefill;

  const trailerPlates = (selection.trailerVehicles || [])
    .map((unit) => unit.vehiclePlate)
    .filter((plate): plate is string => Boolean(plate));

  return {
    // `??` y no `||`: con la selección hecha, una patente vacía es "sale a pie" y no un dato que
    // falta. Sólo cuando no vino ninguna se cae a la del ingreso.
    vehiclePlate: selection.vehiclePlate ?? entrancePrefill.vehiclePlate,
    trailerPlates,
    // Las fichas completas sólo sirven para los acoplados que son los mismos con los que entró.
    trailers: entrancePrefill.trailers.filter((t) => trailerPlates.includes(t.plate)),
    // Sin acoplados en la selección el camión sale solo, y eso es un "no aplica" explícito.
    trailerNotApplicable: trailerPlates.length === 0,
  };
};

export const resolveEntranceAuthorizingCompanyId = (entranceEvent: any): string => {
  const event = entranceEvent?.event || entranceEvent;
  if (!event) return "";

  const [firstAuth] = event.transportAuthorizations || [];
  const fromAuth = typeof firstAuth === "string" ? "" : toId(firstAuth?.company);
  if (fromAuth) return fromAuth;

  const raw = event.company;
  if (Array.isArray(raw)) return toId(raw[0]);
  if (typeof raw === "string") return raw.split(/[,;]/)[0].trim();
  return toId(raw);
};

export const resolveCountryLabel = (code: string | null | undefined): string => {
  if (!code) return "";
  return COUNTRY_LABEL_OVERRIDES[code] ?? countryList.find((c) => c.code === code)?.name ?? code;
};

const fullName = (firstname: string, lastname: string) => `${firstname} ${lastname}`.trim();

/**
 * Anfitrión si la autorización ya fue aprobada; si sigue pendiente (host null) mostramos todos
 * los managers notificados con su estado individual.
 */
const resolvePeople = (auth: TransportAuthorization): AuthorizationPerson[] => {
  const toPerson = (m: {
    user: string;
    firstname: string;
    lastname: string;
    phone: string;
    status: AuthorizationPerson["status"] | null;
  }): AuthorizationPerson => ({
    user: m.user,
    name: fullName(m.firstname, m.lastname),
    phone: m.phone,
    status: m.status ?? "approved",
  });

  if (auth.host) return [toPerson(auth.host)];
  return (auth.managers ?? []).map(toPerson);
};

// `createdAt` viene sin offset de timezone ("2026-08-04T19:08:24"), así que Date.parse lo
// interpreta como hora local y puede desfasar el conteo. Para una solicitud (type "request"),
// `beginTimestamp` coincide con el momento en que se pidió y sí viene en epoch confiable.
const resolveRequestedAt = (auth: TransportAuthorization): number =>
  auth.type === "request"
    ? toTimestampMs(auth.beginTimestamp, auth.beginDatetime)
    : Date.parse(auth.createdAt);

const groupKey = (auth: TransportAuthorization) => `${auth.company ?? ""}|${auth.building ?? ""}`;

const groupSubtitle = (auth: TransportAuthorization) =>
  auth.buildingName ? `${auth.companyName} - ${auth.buildingName}` : auth.companyName;

/** Agrupa preservando el orden de aparición del backend. */
const groupByCompany = (items: TransportAuthorization[]) => {
  const groups = new Map<string, TransportAuthorization[]>();
  items.forEach((auth) => {
    const key = groupKey(auth);
    const existing = groups.get(key);
    if (existing) {
      existing.push(auth);
    } else {
      groups.set(key, [auth]);
    }
  });
  return groups;
};

/** `trailerPlate === null` es el único significado de "No aplica": los consumidores nunca miran
 * `trailerNotApplicable` directamente. */
/** Pedido de dock: nace al elegir una preautorización y cuelga de ella. */
const isDockAssignment = (auth: TransportAuthorization): boolean =>
  auth.requestType === "dockAssignment" && !!auth.parentAuthorization;

const resolveTrailerPlate = (auth: TransportAuthorization): string | null =>
  auth.trailerNotApplicable ? null : auth.trailerPlates?.[0] || null;

/** Los campos de la aprobación por guardia son opcionales en la respuesta: mientras el backend
 * no los mande, `approvedByGuard` queda en false y la caja verde simplemente no se renderiza.
 * `approvedByGuard` viene como id del guardia, no como booleano. */
const resolveGuardApproval = (auth: TransportAuthorization): GuardApprovalInfo => ({
  approvedByGuard: !!auth.approvedByGuard,
  communicationMethod: auth.communicationMethod ?? null,
  authorizedBy: auth.authorizedBy ?? null,
});

const toRow = (auth: TransportAuthorization): AuthorizationRow => {
  const usedTrucks = auth.usedCount ?? 0;
  const totalTrucks = auth.maxTrucks ?? 0;
  return {
    id: auth.id,
    kind: auth.type,
    status: auth.status,
    people: resolvePeople(auth),
    operationLabel: auth.operationType ? OPERATION_LABELS[auth.operationType] : "",
    usedTrucks,
    totalTrucks,
    availableSlots: Math.max(0, totalTrucks - usedTrucks),
    truckUsagePct: totalTrucks > 0 ? Math.round((usedTrucks / totalTrucks) * 100) : 0,
    validFrom: auth.beginDatetime,
    validTo: auth.endDatetime,
    validFromTs: auth.beginTimestamp,
    validToTs: auth.endTimestamp,
    rejectionReason: auth.rejectionReason,
    gateObservations: auth.observations,
    requestedAt: resolveRequestedAt(auth),
    vehiclePlate: auth.vehiclePlate ?? "",
    trailerPlate: resolveTrailerPlate(auth),
    merchandiseInTransit: auth.merchandiseInTransit,
    receipt: auth.receipt ?? undefined,
    seal: auth.seal ?? undefined,
    dock: auth.dock,
    guardApproval: resolveGuardApproval(auth),
    isDockRequest: isDockAssignment(auth),
    parentAuthorizationId: auth.parentAuthorization ?? null,
    isMergedDockRequest: false,
    parentObservations: null,
  };
};

export function buildAuthorizationGroups(items: TransportAuthorization[]): AuthorizationGroup[] {
  const entries = (items ?? []).filter((auth) => auth.eventType === "in");

  return Array.from(groupByCompany(entries).entries()).map(([key, auths]) => {
    const first = auths[0];
    const rows = auths.map(toRow);

    const operationLabels: string[] = [];
    auths.forEach((auth) => {
      if (!auth.operationType) return;
      const label = OPERATION_LABELS[auth.operationType];
      if (!operationLabels.includes(label)) operationLabels.push(label);
    });

    // El vencimiento del grupo es el de la autorización que más lejos llega. No se puede comparar
    // los strings ATOM lexicográficamente porque pueden venir con distinto offset horario.
    const latest = auths.reduce<TransportAuthorization | null>((acc, auth) => {
      if (!acc) return auth;
      const accTs = toTimestampMs(acc.endTimestamp, acc.endDatetime);
      const authTs = toTimestampMs(auth.endTimestamp, auth.endDatetime);
      if (isNaN(authTs)) return acc;
      if (isNaN(accTs) || authTs > accTs) return auth;
      return acc;
    }, null);

    // Sólo mostramos dock si todas las autorizaciones del grupo coinciden; docks mezclados
    // en una única línea del card serían engañosos.
    const docks = auths.map((auth) => auth.dock).filter((dock) => dock !== null && dock !== "");
    const dock =
      docks.length === auths.length && docks.every((d) => d === docks[0])
        ? (docks[0] as string | number)
        : undefined;

    // El encabezado resume la capacidad que el guardia puede usar ahora: una autorización
    // pendiente, rechazada, vencida o ya utilizada no aporta lugares ni camiones al total.
    const usableRows = rows.filter((row) => row.status === "approved");

    // Cantidad de personas autorizantes reales (anfitrión, o managers notificados si está
    // pendiente), no de solicitudes: una fila sin nadie asignado ("Sin autorizador asignado") no
    // debe contar como un autorizador, y si dos filas comparten la misma persona no se duplica.
    const authorizerIds = new Set<string>();
    rows.forEach((row) => row.people.forEach((person) => authorizerIds.add(person.user)));

    return {
      id: key,
      companyId: first.company,
      title: first.companyName,
      subtitle: groupSubtitle(first),
      contactEmail: first.contactEmails?.[0],
      operationLabels,
      usedTrucks: usableRows.reduce((sum, row) => sum + row.usedTrucks, 0),
      totalTrucks: usableRows.reduce((sum, row) => sum + row.totalTrucks, 0),
      availableSlots: usableRows.reduce((sum, row) => sum + row.availableSlots, 0),
      authorizerCount: authorizerIds.size,
      expiresAt: latest?.endDatetime ?? "",
      dock,
      rows,
    };
  });
}

/** Una vez usado/vencido/cancelado, el pedido de dock dejó de estar en juego: la preautorización
 * vuelve a mostrarse sola (con un lugar menos). */
const SPENT_STATUSES: AuthorizationRow["status"][] = ["used", "expired", "canceled"];

/**
 * Fusiona cada pedido de dock que **este** formulario creó con su preautorización, para no
 * mostrar dos tarjetas del mismo camión.
 *
 * La fusión es deliberadamente local: el backend le devuelve el mismo hijo a todos los guardias,
 * y si la dedujéramos de los datos, un guardia vería fusionado el pedido que hizo otro y perdería
 * la preautorización que necesita para arrancar el suyo. Por eso el `Record` viene del estado del
 * formulario y no de la respuesta: al refrescar se pierde, y cada formulario tiene el suyo.
 *
 * La fila resultante es la del hijo — su id es el que manda para seleccionar, reenviar y aprobar —
 * y sólo hereda del padre lo que es del paraguas y no del camión puntual: los totales de camiones
 * y las observaciones para portería.
 */
export function mergeLocalDockRequests(
  groups: AuthorizationGroup[],
  dockRequestByParent: Record<string, string>
): AuthorizationGroup[] {
  if (Object.keys(dockRequestByParent).length === 0) return groups;

  return groups.map((group) => {
    const rowsById = new Map(group.rows.map((row) => [row.id, row]));
    const mergedByParentId = new Map<string, AuthorizationRow>();
    const absorbedChildIds = new Set<string>();

    group.rows.forEach((row) => {
      const childId = dockRequestByParent[row.id];
      const child = childId ? rowsById.get(childId) : undefined;
      // Sin hijo todavía en la respuesta, o ya consumido: el padre se muestra tal cual.
      if (!child || SPENT_STATUSES.includes(child.status)) return;

      absorbedChildIds.add(child.id);
      mergedByParentId.set(row.id, {
        ...child,
        usedTrucks: row.usedTrucks,
        totalTrucks: row.totalTrucks,
        availableSlots: row.availableSlots,
        truckUsagePct: row.truckUsagePct,
        isMergedDockRequest: true,
        parentObservations: row.gateObservations,
      });
    });

    if (mergedByParentId.size === 0) return group;

    // El padre pasa a mostrar la fila fusionada y el hijo desaparece de su propia posición.
    // Cualquier otro pedido de dock — el que hizo otro guardia — conserva su tarjeta.
    const rows = group.rows.reduce<AuthorizationRow[]>((acc, row) => {
      const merged = mergedByParentId.get(row.id);
      if (merged) acc.push(merged);
      else if (!absorbedChildIds.has(row.id)) acc.push(row);
      return acc;
    }, []);

    return { ...group, rows };
  });
}

/** Datos que acompañan al control en la fila de "Verificar controles": el número de
 * remito/precinto a cotejar, o la patente y la empresa del custodio que espera afuera. */
const resolveControlValues = (
  key: BackendControlKey,
  auth: TransportAuthorization
): Pick<GateControlItem, "value" | "valueSecondary"> => {
  if (key === "receipt") return { value: auth.receipt ? `N° ${auth.receipt}` : undefined };
  if (key === "seal") return { value: auth.seal ? `N° ${auth.seal}` : undefined };
  if (key === "vehicleCustody") {
    const plate = auth.vehicleCustodyPlate?.trim();
    if (!plate) return {};
    return { value: plate, valueSecondary: auth.vehicleCustodyCompany?.trim() || undefined };
  }
  return {};
};

const toExitAuthorization = (auth: TransportAuthorization): ExitAuthorization => {
  const people = resolvePeople(auth);
  const trailerPlate = resolveTrailerPlate(auth);
  // El camión puede traer más de un acoplado (bitrén): a diferencia de `trailerPlate` (sólo el
  // primero, que es lo único que miran las vistas de una patente), acá se listan todos para que el
  // guardia los verifique uno por uno.
  const trailerPlates = auth.trailerNotApplicable ? [] : (auth.trailerPlates ?? []).filter(Boolean);

  // Las patentes se verifican siempre; a diferencia de remito/precinto/custodia, no dependen de
  // lo que haya marcado el autorizante en `controlChecks`.
  const plateControls: GateControlItem[] = [
    {
      id: "vehiclePlate",
      label: "Patente tractor",
      value: auth.vehiclePlate ?? "",
      verified: false,
    },
    ...trailerPlates.map((plate, index) => ({
      id: (index === 0 ? "trailerPlate" : (`trailerPlate-${index + 1}` as const)) as GateControlKey,
      label: trailerPlates.length > 1 ? `Patente acoplado ${index + 1}` : "Patente acoplado",
      value: plate,
      verified: false,
    })),
  ];
  const backendControls: GateControlItem[] = CONTROL_ORDER.filter(
    (key) => auth.controlChecks?.[key]
  ).map((key) => ({
    id: key,
    label: CONTROL_LABELS[key],
    ...resolveControlValues(key, auth),
    verified: false,
  }));
  const controls: GateControlItem[] = [...plateControls, ...backendControls];

  return {
    id: auth.id,
    kind: auth.type,
    status: auth.status,
    companyId: auth.company,
    title: auth.companyName,
    subtitle: groupSubtitle(auth),
    contactEmail: auth.contactEmails?.[0],
    hostName: people[0]?.name ?? "—",
    hostPhone: people[0]?.phone ?? "—",
    people,
    validFrom: auth.beginDatetime,
    validTo: auth.endDatetime,
    validFromTs: auth.beginTimestamp,
    validToTs: auth.endTimestamp,
    vehiclePlate: auth.vehiclePlate ?? "",
    trailerPlate,
    driverDni: auth.driverDni ?? "",
    driverCountry: resolveCountryLabel(auth.driverCountryCode),
    loaded: auth.loaded ?? false,
    merchandiseInTransit: auth.merchandiseInTransit,
    receipt: auth.receipt ?? undefined,
    seal: auth.seal ?? undefined,
    vehicleCustodyPlate: auth.vehicleCustodyPlate?.trim() || null,
    vehicleCustodyCompany: auth.vehicleCustodyCompany?.trim() || null,
    controls,
    gateObservations: auth.observations ?? undefined,
    rejectionReason: auth.rejectionReason,
    requestedAt: resolveRequestedAt(auth),
    guardApproval: resolveGuardApproval(auth),
  };
};

export function buildExitAuthorizationGroups(
  items: TransportAuthorization[]
): ExitAuthorizationGroup[] {
  const exits = (items ?? []).filter((auth) => auth.eventType === "out");

  return Array.from(groupByCompany(exits).entries()).map(([key, auths]) => {
    const first = auths[0];
    return {
      id: key,
      title: first.companyName,
      subtitle: groupSubtitle(first),
      contactEmail: first.contactEmails?.[0],
      authorizations: auths.map(toExitAuthorization),
    };
  });
}
